import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { isEmpty, intersection, pluck, sum, isNil, includes } from 'ramda';
import { Helmet } from 'react-helmet';
import { MdFilterList } from 'react-icons/md';
import ReactPixel from 'react-facebook-pixel';

import { oxford } from '../utilities/string';
import expiringStorage from '../utilities/expiringStorage';

import Page from './Page';
import BoardgameListItem from './BoardgameListItem';
import BoardgamesSelectionHero from '../containers/BoardgamesSelectionHero';
import FiltersModal from './FiltersModal';

class BoardgamesListPage extends Component {
  constructor(props) {
    super(props);

    this.onChangeQuery = this.onChangeQuery.bind(this);
    this.onClearQueryClick = this.onClearQueryClick.bind(this);
    this.onChangeBoardgamesSorting = this.onChangeBoardgamesSorting.bind(this);
    this.onFilter = this.onFilter.bind(this);
    this.onFiltersModalClose = this.onFiltersModalClose.bind(this);
    this.onFiltersButtonClick = this.onFiltersButtonClick.bind(this);
    this.getFiltersArray = this.getFiltersArray.bind(this);
    this.getFiltersCount = this.getFiltersCount.bind(this);
    this.getActiveFiltersMessage = this.getActiveFiltersMessage.bind(this);
    this.getNumberOfPlayersFilterMessage = this.getNumberOfPlayersFilterMessage.bind(this);
    this.getCategoriesFilterMessage = this.getCategoriesFilterMessage.bind(this);
    this.getDurationFilterMessage = this.getDurationFilterMessage.bind(this);

    const cachedFilters = expiringStorage.get('filters');
    const filters = cachedFilters
      ? cachedFilters
      : {
        numberOfPlayers: '',
        duration: null,
        categories: [],
      };

    this.state = {
      orderBy: 'title',
      query: '',
      filtersModalOpen: false,
      ...filters,
    };

    this.queryRef = React.createRef();
  }

  componentDidMount() {
    ReactPixel.pageView();
    this.props.getVisibleBoardgames(this.state.orderBy);
    this.props.getCategories();
  }

  componentWillUnmount() {
    this.props.unsubscribeToVisibleBoardgames();
  }

  onChangeBoardgamesSorting(event) {
    event.preventDefault();

    this.setState({ orderBy: event.target.value }, () => {
      this.props.getVisibleBoardgames(this.state.orderBy);
    });
  }

  onChangeQuery(event) {
    event.preventDefault();
    event.persist();
    this.setState({ query: event.target.value || '' });
  }

  onClearQueryClick() {
    this.setState({ query: '' });
    if (this.queryRef) {
      this.queryRef.current.focus();
    }
  }

  onFiltersButtonClick() {
    this.setState({
      filtersModalOpen: true,
    });
  }

  onFilter(filters) {
    this.setState({
      filtersModalOpen: false,
      ...filters,
    });
    
    expiringStorage.set('filters', filters, 60);
  }

  onFiltersModalClose() {
    this.setState({
      filtersModalOpen: false,
    });
  }

  getFiltersArray() {
    return [this.state.categories, this.state.numberOfPlayers, this.state.duration];
  }

  getFiltersCount() {
    return sum(this.getFiltersArray()
      .map(filter => !isNil(filter) && !isEmpty(filter))
      .map(isNotEmpty => isNotEmpty ? 1 : 0));
  }
  
  getActiveFiltersMessage() {
    const filtersMessage = oxford([
      this.getNumberOfPlayersFilterMessage(),
      this.getCategoriesFilterMessage(),
      this.getDurationFilterMessage(),
    ].filter(message => !isNil(message)), 'e');

    return `Stai visualizzando tutti i giochi ${filtersMessage}.`;
  }

  getNumberOfPlayersFilterMessage() {
    return !isEmpty(this.state.numberOfPlayers)
      ? `per ${this.state.numberOfPlayers} giocatori`
      : null;
  }

  getCategoriesFilterMessage() {
    const { categories } = this.state;
    if (isEmpty(categories)) {
      return null;
    }

    const prefix = categories.length > 1 ? 'alle categorie' : 'alla categoria';

    const categoriesList = oxford(pluck('name', this.props.categories
      .filter(category => includes(category.id, categories))
    ), 'o');

    return `che appartengono ${prefix} ${categoriesList}`;
  }

  getDurationFilterMessage() {
    const { duration } = this.state;
    
    if (isNil(duration)) {
      return null;
    }

    if (duration.min <= 0) {
      return `della durata massima di ${duration.max} minuti`;
    }

    if (duration.max === Number.MAX_SAFE_INTEGER) {
      return `della durata minima di ${duration.min - 1} minuti`;
    }

    return `della durata compresa tra ${duration.min - 1} e ${duration.max} minuti`;
  }

  render() {
    const filteredBoardgames = this.props.boardgames
      .filter(bg => bg.title.toLocaleLowerCase().includes(this.state.query.trim().toLocaleLowerCase()))
      .filter(bg => isEmpty(this.state.categories) || !isEmpty(intersection(this.state.categories, pluck('id', bg.categories))))
      .filter(bg => isNil(this.state.duration) || (bg.minPlayTime >= this.state.duration.min && bg.minPlayTime <= this.state.duration.max))
      .filter(bg => {
        return isEmpty(this.state.numberOfPlayers) || (this.state.numberOfPlayers >= bg.minPlayers && this.state.numberOfPlayers <= (bg.maxPlayers || bg.minPlayers));
      });

    const filtersCount = this.getFiltersCount();

    return (
      <React.Fragment>
        <Helmet>
          <title>La Libreria - Smart Gaming</title>
        </Helmet>
        <Page heroComponent={BoardgamesSelectionHero}>
          
          <div className="flex justify-center flex-col md:flex-row p-4 md:w-2/3 w-full mx-auto mb-2 md:mb-4">
            <div className="md:inline-block relative w-full md:pl-2 md:mt-0 md:mr-3">
              <div className="block relative">
                <div className="absolute inset-y-0 left-0 flex items-center px-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                    <path fill="#cbd5e0" d="M23.832 19.641l-6.821-6.821c2.834-5.878-1.45-12.82-8.065-12.82-4.932 0-8.946 4.014-8.946 8.947 0 6.508 6.739 10.798 12.601 8.166l6.879 6.879c1.957.164 4.52-2.326 4.352-4.351zm-14.886-4.721c-3.293 0-5.973-2.68-5.973-5.973s2.68-5.973 5.973-5.973c3.294 0 5.974 2.68 5.974 5.973s-2.68 5.973-5.974 5.973z"/>
                  </svg>
                </div>
                <input
                  className="appearance-none border border-gray-400 rounded-full w-full py-2 px-10 text-gray-700 leading-tight focus:outline-none focus:shadow"
                  type="text"
                  placeholder="Cerca"
                  value={this.state.query}
                  onChange={this.onChangeQuery}
                  ref={this.queryRef}
                />
                <button type="button" onClick={this.onClearQueryClick} className="absolute inset-y-0 right-0 flex items-center px-4 cursor-pointer text-gray-700">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                    <path fill="#cbd5e0" d="M23.954 21.03l-9.184-9.095 9.092-9.174-2.832-2.807-9.09 9.179-9.176-9.088-2.81 2.81 9.186 9.105-9.095 9.184 2.81 2.81 9.112-9.192 9.18 9.1z"/>
                  </svg>
                </button>
              </div>
            </div>
            <div className="relative items-center w-full md:w-auto">
              <button onClick={this.onFiltersButtonClick}
                className="appearance-none w-full md:w-auto focus:outline-none focus:shadow border border-gray-600 bg-gray-600 text-white hover:bg-white hover:text-gray-600 flex justify-center items-center rounded-full px-12 py-2 mt-3 md:mt-0"
                type="button">
                  {
                    filtersCount > 0
                      ? <span className="text-xs text-center mr-2 text-white font-bold bg-red-500 rounded-full w-5 h-5">{ filtersCount }</span>
                      : <MdFilterList className="mr-2" />
                  }
                  <span>Filtra</span>
              </button>
            </div>
          </div>

          <div className="flex justify-end md:mb-6">
            <div className="relative flex">
              <div className="block relative">
                <select
                  id="order_by"
                  className="appearance-none pr-5 py-1 rounded-none bg-transparent text-gray-700 leading-tight focus:outline-none border-b-2 border-transparent focus:border-black"
                  value={this.state.orderBy}
                  onChange={this.onChangeBoardgamesSorting}>
                  <option value="title">Ordina per nome</option>
                  <option value="playsCount">Ordina per popolarità</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center text-gray-darker">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                </div>
              </div>
            </div>
          </div>
          {
            this.getFiltersCount() > 0 && filteredBoardgames.length > 0 && 
              <div className="flex justify-center mb-8 mt-8">
                <p className="text-center max-w-3/5 font-medium">{ this.getActiveFiltersMessage() }</p>
              </div>
          }
          <div className="flex flex-wrap justify-center">
            {
              filteredBoardgames
                .map(bg => (
                  <Link key={bg.id} className="w-full md:w-2/5 lg:w-1/4 md:mx-4 md:mb-8" to={`/boardgames/${bg.id}`}>
                    <BoardgameListItem boardgame={bg} />
                  </Link>
                ))
            }
            {
              filteredBoardgames.length === 0 && !this.props.loading &&
                <div>
                  <h1 className="font-serif text-xl py-4 text-center font-bold">Purtroppo non abbiamo nessun gioco che corrisponde ai tuoi criteri di ricerca</h1>
                  <p className="text-center">Prova a cambiare il numero di giocatori oppure il testo della ricerca.</p>
                </div>
            }
          </div>
        </Page>

        <FiltersModal
          isOpen={this.state.filtersModalOpen}
          maxNumberOfPlayers={this.props.maxNumberOfPlayers}
          categoryOptions={this.props.categories}
          categories={this.state.categories}
          duration={this.state.duration}
          numberOfPlayers={this.state.numberOfPlayers}
          onFilter={this.onFilter}
          onClose={this.onFiltersModalClose} />
        
      </React.Fragment>
    );
  }
}

export default BoardgamesListPage;
